import React from 'react';

import { TFunction, useTranslation } from 'react-i18next';
import { Label } from 'semantic-ui-react';

import {
  BasePrice,
  PriceType,
  TariffComponent as TariffComponentType,
  TimePrice,
  Price,
} from 'subdomains/cpo/types/charging-policy';
import { currencyToSymbol } from 'utils/formatting';

type Props = {
  price?: Price;
  currency: string;
};

export const PriceLabel = ({ price, currency }: Props) => {
  const { t } = useTranslation();
  if (!price) {
    return null;
  }

  return (
    <Label basic>
      {formatPriceType(t, price)}: {formatPriceValue(t, currency, price)}
    </Label>
  );
};

function formatPriceType(
  t: TFunction<'translation', undefined>,
  price: TariffComponentType['price']
): string {
  if (!price) return '';

  switch (price?.type) {
    case PriceType.ENERGY:
      return t('tariff.priceLabel.priceTypeEnergy', 'Energy price');
    case PriceType.TIME:
      return t('tariff.priceLabel.priceTypeTime', 'Time price');
    case PriceType.PARKING_TIME:
      return t('tariff.priceLabel.priceTypeParkingTime', 'Parking time price');
    case PriceType.FLAT:
      return t('tariff.priceLabel.priceTypeFlat', 'Session price');
    default:
      return t('tariff.priceLabel.priceTypeUnknown', 'Unknown price type');
  }
}

function formatPriceValue(
  t: TFunction<'translation', undefined>,
  currency: string,
  price: TariffComponentType['price']
): string {
  if (!price) return '';

  const currencySymbol = currencyToSymbol(currency);

  switch (price?.type) {
    case PriceType.ENERGY:
    case PriceType.FLAT:
      return t(
        'tariff.priceLabel.previewBasePriceValue',
        '{{currencySymbol}}{{price}}/{{unit}}',
        {
          currencySymbol,
          price: (price as BasePrice).value,
          unit: price.type === PriceType.ENERGY ? 'kWh' : 'session',
        }
      );
    case PriceType.TIME:
    case PriceType.PARKING_TIME:
      return formatTimePriceValue(t, currencySymbol, price as TimePrice);
    default:
      return '';
  }
}

function formatTimePriceValue(
  t: TFunction<'translation', undefined>,
  currencySymbol: string,
  price: TimePrice
): string {
  const value = price.value;
  const unit = price.unit?.toLowerCase() ?? t('tariff.priceLabel.hour', 'h');

  return t(
    'tariff.priceLabel.previewTimePriceValue',
    '{{currencySymbol}}{{price}}/{{unit}}',
    {
      currencySymbol,
      price: value,
      unit,
    }
  );
}
